import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { Link as RouterLink, useParams, useNavigate } from "react-router";
import {
  Grid as MuiGrid,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Breadcrumbs as MuiBreadcrumbs,
  InputAdornment as MuiInputAdornment,
} from "@mui/material";
import {
  AttachMoney as MuiAttachMoneyIcon,
  NavigateNext as MuiNavigateNextIcon,
} from "@mui/icons-material";
import { useAlerts, useUsers } from "common";
import {
  postFinancialHistory,
  getFiscalYear,
  putFinancialHistory,
  getFinancialHistoryById,
} from "app/services/componentsService";
import { FormButton } from "app/shared/ui/FormButton";

export const FinancialHistoryForm = (props) => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { currentUser } = useUsers();
  const { permissions } = currentUser;
  const { clearAlert, setAlert } = useAlerts();

  // handle close for form
  const handleClose = () => {
    navigate(
      `/commitments/${params.commitmentId}/components/${params.componentId}/financial-history`,
    );
  };

  const _handleSubmit = (values) => {
    params.fiscalYear
      ? putFinancialHistory(
          params.commitmentId,
          params.componentId,
          params.componentPlanId,
          values,
          fiscalYear,
          setLoading,
          clearAlert,
          setAlert,
          navigate,
          t,
        )
      : postFinancialHistory(
          params.commitmentId,
          params.componentId,
          values,
          fiscalYear,
          setLoading,
          clearAlert,
          setAlert,
          navigate,
          t,
        );
  };

  const [fiscalYear, setFiscalYear] = useState(params.fiscalYear || "");

  const [formDataResponse, setFormDataResponse] = useState({
    // Object keys use to create/edit form data
    budgetAmount: "",
    planAmount: "",
    longRangeFinancialForecast: "",
    yepAmount: "",
    fySpendCap: "",
  });

  const [, setLoading] = useState(false);

  const pageType = props.pageType;

  const financialHistoryFormFieldStr =
    "Commitments.financialHistory.create.form.fields";
  const financialHistoryCreateStr = "Commitments.financialHistory.create";
  const financialHistoryEditStr = "Commitments.financialHistory.edit";

  useEffect(() => {
    clearAlert();
    !params.fiscalYear &&
      getFiscalYear(setFiscalYear, params.componentId, setAlert, clearAlert);
    params.componentPlanId &&
      getFinancialHistoryById(
        params.componentPlanId,
        params.commitmentId,
        params.componentId,
        setFormDataResponse,
        clearAlert,
        setAlert,
      );
    // eslint-disable-next-line
  }, [
    params.componentId,
    params.fiscalYear,
    params.commitmentId,
    params.componentPlanId,
  ]);

  const formatCurrency = (value) => {
    if (value) {
      return parseFloat(value).toFixed(2);
    } else {
      if (pageType === "new") {
        return "";
      } else {
        return "0.00";
      }
    }
  };

  return (
    ((props.pageType === "new" &&
      permissions.CREATE_COMPONENT_FINANCIAL_PLAN) ||
      (props.pageType === "edit" &&
        permissions.EDIT_COMPONENT_FINANCIAL_PLAN)) && (
      <Formik
        initialValues={formDataResponse}
        enableReinitialize
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          // on submit handler
          _handleSubmit(values);
        }}
        validationSchema={financialHistoryValidation}
      >
        {(props) => {
          const { values, handleSubmit, setFieldValue, dirty, isValid } = props;
          return (
            <form onSubmit={handleSubmit}>
              <MuiGrid size={12}
                container
                sx={{
                  "&.MuiGrid-root": {
                    margin: "0px 16px",
                  },
                }}
              >
                <MuiGrid size={12}>
                  <MuiBreadcrumbs
                    separator={<MuiNavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                  >
                    <RouterLink
                      style={{ textDecoration: "none" }}
                      to={`/commitments`}
                    >
                      <MuiTypography color="textSecondary">
                        {t("Commitments.mainView.title")}
                      </MuiTypography>
                    </RouterLink>
                    <RouterLink
                      style={{ textDecoration: "none" }}
                      to={`/commitments/${params.commitmentId}/components/${params.componentId}/financial-history`}
                    >
                      <MuiTypography color="textSecondary">
                        {t("Commitments.financialHistory.mainView.title")}
                      </MuiTypography>
                    </RouterLink>
                    <MuiTypography color="textPrimary">
                      {pageType === "new"
                        ? t(`${financialHistoryCreateStr}.breadcrumbTitle`)
                        : t(`${financialHistoryEditStr}.breadcrumbTitle`)}
                    </MuiTypography>
                  </MuiBreadcrumbs>
                </MuiGrid>
                <MuiGrid size={12}>
                  <MuiTypography variant="h1">
                    {pageType === "new"
                      ? `${t(`${financialHistoryCreateStr}.title`, {
                          fiscalYear,
                        })}`
                      : `${t(`${financialHistoryEditStr}.title`, {
                          fiscalYear,
                        })}`}
                  </MuiTypography>
                </MuiGrid>
                <MuiGrid size={12} container spacing={2} sx={{ marginTop: "16px" }}>
                  <MuiGrid size={4}>
                    <NumericFormat
                      id="budgetAmount"
                      label={t(
                        `${financialHistoryFormFieldStr}.budgetAmount.label`,
                      )}
                      customInput={MuiTextField}
                      thousandSeparator
                      decimalScale={2}
                      value={formatCurrency(values.budgetAmount)}
                      valueIsNumericString
                      isAllowed={(values) => {
                        const { value } = values;
                        return (
                          value === "-" ||
                          (value >= -999999999.99 && value <= 999999999.99)
                        );
                      }}
                      allowNegative
                      onValueChange={(values) => {
                        setFieldValue("budgetAmount", values.value);
                      }}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <MuiInputAdornment position="start">
                              <MuiAttachMoneyIcon
                                fontSize="small"
                                color="disabled"
                              />
                            </MuiInputAdornment>
                          ),
                        },
                      }}
                    />
                  </MuiGrid>
                  <MuiGrid size={4}>
                    <NumericFormat
                      id="planAmount"
                      label={t(
                        `${financialHistoryFormFieldStr}.planAmount.label`,
                      )}
                      customInput={MuiTextField}
                      thousandSeparator
                      decimalScale={2}
                      value={formatCurrency(values.planAmount)}
                      isAllowed={(values) => {
                        const { value } = values;
                        return (
                          value === "-" ||
                          (value >= -999999999.99 && value <= 999999999.99)
                        );
                      }}
                      allowNegative
                      onValueChange={(values) => {
                        setFieldValue("planAmount", values.value);
                      }}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <MuiInputAdornment position="start">
                              <MuiAttachMoneyIcon
                                fontSize="small"
                                color="disabled"
                              />
                            </MuiInputAdornment>
                          ),
                        },
                      }}
                    />
                  </MuiGrid>
                  <MuiGrid size={4}>
                    <NumericFormat
                      id="longRangeFinancialForecast"
                      label={t(
                        `${financialHistoryFormFieldStr}.longRangeFinancialForecast.label`,
                      )}
                      customInput={MuiTextField}
                      thousandSeparator
                      decimalScale={2}
                      value={formatCurrency(values.longRangeFinancialForecast)}
                      isAllowed={(values) => {
                        const { value } = values;
                        return (
                          value === "-" ||
                          (value >= -999999999.99 && value <= 999999999.99)
                        );
                      }}
                      allowNegative
                      onValueChange={(values) => {
                        setFieldValue(
                          "longRangeFinancialForecast",
                          values.value,
                        );
                      }}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <MuiInputAdornment position="start">
                              <MuiAttachMoneyIcon
                                fontSize="small"
                                color="disabled"
                              />
                            </MuiInputAdornment>
                          ),
                        },
                      }}
                    />
                  </MuiGrid>
                </MuiGrid>
                <MuiGrid size={12} container spacing={2} sx={{ marginTop: "16px" }}>
                  <MuiGrid size={4}>
                    <NumericFormat
                      id="yepAmount"
                      label={t(
                        `${financialHistoryFormFieldStr}.yepAmount.label`,
                      )}
                      customInput={MuiTextField}
                      thousandSeparator
                      decimalScale={2}
                      value={formatCurrency(values.yepAmount)}
                      isAllowed={(values) => {
                        const { value } = values;
                        return (
                          value === "-" ||
                          (value >= -999999999.99 && value <= 999999999.99)
                        );
                      }}
                      allowNegative
                      onValueChange={(values) => {
                        setFieldValue("yepAmount", values.value);
                      }}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <MuiInputAdornment position="start">
                              <MuiAttachMoneyIcon
                                fontSize="small"
                                color="disabled"
                              />
                            </MuiInputAdornment>
                          ),
                        },
                      }}
                    />
                  </MuiGrid>
                  <MuiGrid size={4}>
                    <NumericFormat
                      id="fySpendCap"
                      label={t(
                        `${financialHistoryFormFieldStr}.fySpendCap.label`,
                      )}
                      customInput={MuiTextField}
                      thousandSeparator
                      decimalScale={2}
                      value={formatCurrency(values.fySpendCap)}
                      isAllowed={(values) => {
                        const { value } = values;
                        return (
                          value === "-" ||
                          (value >= -999999999.99 && value <= 999999999.99)
                        );
                      }}
                      allowNegative
                      onValueChange={(values) => {
                        setFieldValue("fySpendCap", values.value);
                      }}
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <MuiInputAdornment position="start">
                              <MuiAttachMoneyIcon
                                fontSize="small"
                                color="disabled"
                              />
                            </MuiInputAdornment>
                          ),
                        },
                      }}
                    />
                  </MuiGrid>
                  <MuiGrid
                    size={4}
                    container
                    sx={{
                      justifyContent: "flex-end",
                    }}
                  >
                    <MuiGrid
                      container
                      size={7}
                      spacing={2}
                      sx={{
                        justifyContent: "flex-end",
                      }}
                    >
                      <FormButton
                        cancel={handleClose}
                        save={{ disabled: !dirty || !isValid }}
                      />
                    </MuiGrid>
                  </MuiGrid>
                </MuiGrid>
              </MuiGrid>
            </form>
          );
        }}
      </Formik>
    )
  );
};

const financialHistoryValidation = Yup.object().shape(
  {
    budgetAmount: Yup.number().when(
      ["planAmount", "longRangeFinancialForecast", "yepAmount", "fySpendCap"],
      {
        is: (planAmount, longRangeFinancialForecast, yepAmount, fySpendCap) =>
          !planAmount &&
          !longRangeFinancialForecast &&
          !yepAmount &&
          !fySpendCap,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      },
    ),
    planAmount: Yup.number().when(
      ["budgetAmount", "longRangeFinancialForecast", "yepAmount", "fySpendCap"],
      {
        is: (budgetAmount, longRangeFinancialForecast, yepAmount, fySpendCap) =>
          !budgetAmount &&
          !longRangeFinancialForecast &&
          !yepAmount &&
          !fySpendCap,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      },
    ),
    longRangeFinancialForecast: Yup.number().when(
      ["budgetAmount", "planAmount", "yepAmount", "fySpendCap"],
      {
        is: (budgetAmount, planAmount, yepAmount, fySpendCap) =>
          !budgetAmount && !planAmount && !yepAmount && !fySpendCap,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      },
    ),
    yepAmount: Yup.number().when(
      [
        "budgetAmount",
        "planAmount",
        "longRangeFinancialForecast",
        "fySpendCap",
      ],
      {
        is: (
          budgetAmount,
          planAmount,
          longRangeFinancialForecast,
          fySpendCap,
        ) =>
          !budgetAmount &&
          !planAmount &&
          !longRangeFinancialForecast &&
          !fySpendCap,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      },
    ),
    fySpendCap: Yup.number().when(
      ["budgetAmount", "planAmount", "longRangeFinancialForecast", "yepAmount"],
      {
        is: (budgetAmount, planAmount, longRangeFinancialForecast, yepAmount) =>
          !budgetAmount &&
          !planAmount &&
          !longRangeFinancialForecast &&
          !yepAmount,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      },
    ),
  },
  [
    ["budgetAmount", "planAmount"],
    ["budgetAmount", "longRangeFinancialForecast"],
    ["budgetAmount", "yepAmount"],
    ["budgetAmount", "fySpendCap"],
    ["planAmount", "longRangeFinancialForecast"],
    ["planAmount", "yepAmount"],
    ["planAmount", "fySpendCap"],
    ["longRangeFinancialForecast", "yepAmount"],
    ["longRangeFinancialForecast", "fySpendCap"],
    ["yepAmount", "fySpendCap"],
  ],
);
